export type ConsentDecision = 'accepted' | 'declined';

export type ParsedConsent = {
    decision: ConsentDecision;
    at: number;
    expiresAt: number;
};

export const COOKIE_CONSENT_STORAGE_KEY = 'grm-cookie-consent-v1';
export const COOKIE_CONSENT_SESSION_KEY = 'grm-cookie-consent-failclosed-v1';
export const COOKIE_CONSENT_FAILCLOSED_COOKIE = 'grm_cookie_consent_failclosed';
export const COOKIE_CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
export const COOKIE_SETTINGS_OPEN_EVENT = 'grm:open-cookie-settings';

const MAX_TIMEOUT_MS = 2_147_483_647;
const STORAGE_PROBE_KEY = 'grm-cookie-consent-probe-v1';

type ConsentListener = (decision: ConsentDecision | null) => void;

let current: ParsedConsent | null = null;
let isInitialized = false;
let expiryTimer: ReturnType<typeof setTimeout> | null = null;
let storageListenerAttached = false;
const listeners = new Set<ConsentListener>();

const isDecision = (value: unknown): value is ConsentDecision =>
    value === 'accepted' || value === 'declined';

export function parseConsentRecord(raw: string | null, now = Date.now()): ParsedConsent | null {
    if (raw === null) return null;

    try {
        const value: unknown = JSON.parse(raw);
        if (typeof value !== 'object' || value === null) return null;

        const { decision, at } = value as { decision?: unknown; at?: unknown };
        if (!isDecision(decision) || typeof at !== 'string') return null;

        const timestamp = Date.parse(at);
        if (!Number.isFinite(timestamp) || timestamp > now) return null;
        if (now - timestamp >= COOKIE_CONSENT_MAX_AGE_MS) return null;

        return {
            decision,
            at: timestamp,
            expiresAt: timestamp + COOKIE_CONSENT_MAX_AGE_MS,
        };
    } catch {
        return null;
    }
}

function readStorage(storage: Storage | undefined, key: string): string | null {
    if (storage === undefined) return null;
    try {
        return storage.getItem(key);
    } catch {
        return null;
    }
}

function writeStorage(storage: Storage | undefined, key: string, value: string): boolean {
    if (storage === undefined) return false;
    try {
        storage.setItem(key, value);
        return true;
    } catch {
        return false;
    }
}

function removeStorage(storage: Storage | undefined, key: string): boolean {
    if (storage === undefined) return false;
    try {
        storage.removeItem(key);
        return true;
    } catch {
        return false;
    }
}

function isStorageWritable(storage: Storage | undefined): boolean {
    if (storage === undefined) return false;

    const probe = String(Date.now());
    try {
        storage.setItem(STORAGE_PROBE_KEY, probe);
        const restored = storage.getItem(STORAGE_PROBE_KEY);
        storage.removeItem(STORAGE_PROBE_KEY);
        return restored === probe;
    } catch {
        try {
            storage.removeItem(STORAGE_PROBE_KEY);
        } catch {
            /* A read-only store is exactly what this probe is meant to detect. */
        }
        return false;
    }
}

function getLocalStorage(): Storage | undefined {
    if (typeof window === 'undefined') return undefined;
    try {
        return window.localStorage;
    } catch {
        return undefined;
    }
}

function getSessionStorage(): Storage | undefined {
    if (typeof window === 'undefined') return undefined;
    try {
        return window.sessionStorage;
    } catch {
        return undefined;
    }
}

function readFailclosedCookie(): string | null {
    if (typeof document === 'undefined') return null;
    try {
        const match = document.cookie.match(
            new RegExp(`(?:^|;)\\s*${COOKIE_CONSENT_FAILCLOSED_COOKIE}=([^;]*)`),
        );
        return match ? decodeURIComponent(match[1]) : null;
    } catch {
        return null;
    }
}

function readFailclosedRecord(now: number): ParsedConsent | null {
    const sessionRecord = parseConsentRecord(
        readStorage(getSessionStorage(), COOKIE_CONSENT_SESSION_KEY),
        now,
    );
    if (sessionRecord?.decision === 'declined') return sessionRecord;

    const cookieRecord = parseConsentRecord(readFailclosedCookie(), now);
    return cookieRecord?.decision === 'declined' ? cookieRecord : null;
}

function writeFailclosedCookie(serialized: string): boolean {
    if (typeof document === 'undefined') return false;
    try {
        document.cookie = `${COOKIE_CONSENT_FAILCLOSED_COOKIE}=${encodeURIComponent(
            serialized,
        )}; Max-Age=${Math.floor(COOKIE_CONSENT_MAX_AGE_MS / 1000)}; path=/; SameSite=Lax`;
        return true;
    } catch {
        return false;
    }
}

function clearFailclosedCookie(): void {
    if (typeof document === 'undefined') return;
    try {
        document.cookie = `${COOKIE_CONSENT_FAILCLOSED_COOKIE}=; Max-Age=0; path=/; SameSite=Lax`;
    } catch {
        /* Nothing else can be done if cookie writes are unavailable. */
    }
}

function readStoredConsent(now: number): ParsedConsent | null {
    /* A failed decline writes this same-tab tombstone. It must win over an
       accepted record that could not be removed from localStorage. */
    const failclosed = readFailclosedRecord(now);
    if (failclosed !== null) return failclosed;

    const local = getLocalStorage();
    const stored = parseConsentRecord(readStorage(local, COOKIE_CONSENT_STORAGE_KEY), now);
    if (stored === null) return null;

    /* A readable accepted record in a store that refuses writes/removals can
       never be revoked by this tab. Refuse to boot tracking from it. An
       explicit accept in the current document still works in memory. */
    if (stored.decision === 'accepted' && !isStorageWritable(local)) return null;

    return stored;
}

function scheduleExpiry(record: ParsedConsent | null): void {
    if (expiryTimer !== null) {
        clearTimeout(expiryTimer);
        expiryTimer = null;
    }
    if (record === null || typeof window === 'undefined') return;

    const remaining = record.expiresAt - Date.now();
    if (remaining <= 0) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        expireConsent();
        return;
    }

    expiryTimer = setTimeout(() => {
        expiryTimer = null;
        if (current !== null && Date.now() >= current.expiresAt) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            expireConsent();
        } else {
            scheduleExpiry(current);
        }
    }, Math.min(remaining, MAX_TIMEOUT_MS));
}

function notify(): void {
    listeners.forEach((listener) => listener(current?.decision ?? null));
}

function applyRecord(record: ParsedConsent | null, notifyListeners = true): void {
    current = record;
    scheduleExpiry(record);
    if (notifyListeners) notify();
}

function recordFor(decision: ConsentDecision, now: number): ParsedConsent {
    return {
        decision,
        at: now,
        expiresAt: now + COOKIE_CONSENT_MAX_AGE_MS,
    };
}

export function initializeConsent(): void {
    if (typeof window === 'undefined') return;

    if (!isInitialized) {
        isInitialized = true;
        current = readStoredConsent(Date.now());
        scheduleExpiry(current);
    }

    if (!storageListenerAttached) {
        storageListenerAttached = true;
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        window.addEventListener('storage', handleStorageChange);
    }
}

function handleStorageChange(event: StorageEvent): void {
    if (event.key !== null && event.key !== COOKIE_CONSENT_STORAGE_KEY) return;
    if (!isInitialized) initializeConsent();

    const now = Date.now();
    const failclosed = readFailclosedRecord(now);
    if (failclosed !== null) {
        if (current?.decision !== 'declined') applyRecord(failclosed);
        return;
    }

    const next = event.key === null ? null : parseConsentRecord(event.newValue, now);

    if (current?.decision === next?.decision) {
        current = next;
        scheduleExpiry(next);
        return;
    }

    applyRecord(next);
}

export function readConsent(now = Date.now()): ConsentDecision | null {
    return readStoredConsent(now)?.decision ?? null;
}

export function getConsentDecision(now = Date.now()): ConsentDecision | null {
    initializeConsent();

    if (current !== null && now >= current.expiresAt) {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        expireConsent();
        return null;
    }

    return current?.decision ?? null;
}

export function hasAnalyticsConsent(now = Date.now()): boolean {
    return getConsentDecision(now) === 'accepted';
}

export function subscribeConsent(listener: ConsentListener): () => void {
    initializeConsent();
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function saveConsent(decision: ConsentDecision, now = Date.now()): void {
    initializeConsent();

    const record = recordFor(decision, now);
    const local = getLocalStorage();
    const session = getSessionStorage();
    const serialized = JSON.stringify({
        decision,
        at: new Date(now).toISOString(),
    });

    /* Change the in-memory decision before touching storage so a synchronous
       throw or a listener can never observe the previous accepted state. */
    current = record;

    if (decision === 'declined') {
        const persisted = writeStorage(local, COOKIE_CONSENT_STORAGE_KEY, serialized);
        if (persisted) {
            removeStorage(session, COOKIE_CONSENT_SESSION_KEY);
            clearFailclosedCookie();
        } else {
            /* Do not leave an accepted value behind. If even removal fails,
               same-tab and cookie tombstones keep a reload fail-closed. */
            removeStorage(local, COOKIE_CONSENT_STORAGE_KEY);
            writeStorage(session, COOKIE_CONSENT_SESSION_KEY, serialized);
            writeFailclosedCookie(serialized);
        }
    } else {
        /* A new explicit accept must retire an older failed-decline tombstone.
           If removal is refused, overwrite it with the accepted record: readers
           only honor declined tombstones. */
        if (!removeStorage(session, COOKIE_CONSENT_SESSION_KEY)) {
            writeStorage(session, COOKIE_CONSENT_SESSION_KEY, serialized);
        }
        clearFailclosedCookie();
        if (readFailclosedCookie() !== null) {
            writeFailclosedCookie(serialized);
        }
        writeStorage(local, COOKIE_CONSENT_STORAGE_KEY, serialized);
    }

    scheduleExpiry(record);
    notify();
}

export function acceptConsent(now = Date.now()): void {
    saveConsent('accepted', now);
}

export function declineConsent(now = Date.now()): void {
    saveConsent('declined', now);
}

export function expireConsent(now = Date.now()): void {
    if (current !== null && now < current.expiresAt) return;

    removeStorage(getLocalStorage(), COOKIE_CONSENT_STORAGE_KEY);
    removeStorage(getSessionStorage(), COOKIE_CONSENT_SESSION_KEY);
    clearFailclosedCookie();
    applyRecord(null);
}

export function openCookieSettings(): void {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(COOKIE_SETTINGS_OPEN_EVENT));
}

export function onCookieSettingsOpen(listener: () => void): () => void {
    if (typeof window === 'undefined') return () => undefined;
    window.addEventListener(COOKIE_SETTINGS_OPEN_EVENT, listener);
    return () => window.removeEventListener(COOKIE_SETTINGS_OPEN_EVENT, listener);
}

/* Keeps the browser test harness isolated without exposing state in production. */
export function resetConsentStateForTests(): void {
    if (expiryTimer !== null) {
        clearTimeout(expiryTimer);
        expiryTimer = null;
    }
    listeners.clear();
    isInitialized = false;
    current = null;
}
